import React, { useState, useEffect, useCallback } from "react";
import { useParams, useHistory, useLocation } from "react-router-dom";

import openSocket from "../../services/socket-io";
import clsx from "clsx";

import { Paper, makeStyles } from "@material-ui/core";

import ContactDrawer from "../ContactDrawer";
import MessageInput from "../MessageInput/";
import TicketHeader from "../TicketHeader";
import TicketInfo from "../TicketInfo";
import TicketActionButtons from "../TicketActionButtons";
import MessagesList from "../MessagesList";
import ChatSearchBar from "../ChatSearchBar";
import api from "../../services/api";
import { ReplyMessageProvider } from "../../context/ReplyingMessage/ReplyingMessageContext";
import toastError from "../../errors/toastError";
import useErpClient from "../../hooks/useErpClient";

const drawerWidth = 320;

const useStyles = makeStyles((theme) => ({
  root: {
    display: "flex",
    height: "100%",
    minHeight: 0,
    position: "relative",
    overflow: "hidden",
  },

  chatBody: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    minHeight: 0,
    overflow: "hidden",
  },

  mainWrapper: {
    flex: 1,
    minHeight: 0,
    height: "100%",
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
    borderTopLeftRadius: 0,
    borderBottomLeftRadius: 0,
    borderLeft: "0",
    marginRight: -drawerWidth,
    transition: theme.transitions.create("margin", {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
  },

  mainWrapperShift: {
    borderTopRightRadius: 0,
    borderBottomRightRadius: 0,
    transition: theme.transitions.create("margin", {
      easing: theme.transitions.easing.easeOut,
      duration: theme.transitions.duration.enteringScreen,
    }),
    marginRight: 0,
  },
}));

const Ticket = ({ onReturnToQueue }) => {
  const { ticketId } = useParams();
  const history = useHistory();
  const location = useLocation();
  const messageIdFromSearch = React.useMemo(() => {
    const params = new URLSearchParams(location.search || "");
    return params.get("messageId") || null;
  }, [location.search]);
  const classes = useStyles();

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [contact, setContact] = useState({});
  const [ticket, setTicket] = useState({});
  const [chatSearchOpen, setChatSearchOpen] = useState(false);
  const [inChatSearchMessageId, setInChatSearchMessageId] = useState(null);

  const {
    erpConfigured,
    erpClient,
    erpClients,
    selectErpClient,
    erpFrontendUrl,
    erpPendingCounts,
    loading: erpLoading
  } = useErpClient(contact?.number);

  const ticketMatches = String(ticket?.id) === String(ticketId);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setContact({});
    setTicket({});
    setChatSearchOpen(false);
    setInChatSearchMessageId(null);

    const fetchTicket = async () => {
      try {
        const { data } = await api.get("/tickets/" + ticketId);
        if (cancelled) return;

        setContact(data.contact);
        setTicket(data);
        setLoading(false);

        try {
          await api.put(`/tickets/${ticketId}/read/internal`);
        } catch (err) {
          // Do not block UI on this
          // eslint-disable-next-line no-console
          console.error("Error marking internal unread as read", err);
        }
      } catch (err) {
        if (!cancelled) {
          setLoading(false);
          toastError(err);
        }
      }
    };
    fetchTicket();
    return () => {
      cancelled = true;
    };
  }, [ticketId]);

  useEffect(() => {
    const socket = openSocket();
    if (!socket) return undefined;

    const join = () => socket.emit("joinChatBox", String(ticketId));
    if (socket.connected) join();
    socket.on("connect", join);

    const onTicket = (data) => {
      if (data.action === "update" && data.ticket && String(data.ticket.id) === String(ticketId)) {
        setTicket((prev) => ({ ...prev, ...data.ticket }));
      }

      // Shared socket also receives deletes for other tickets in the same status room.
      // Only leave this chat when THIS ticket was removed or left the agent.
      if (
        data.action === "delete" &&
        data.ticketId != null &&
        String(data.ticketId) === String(ticketId)
      ) {
        history.push("/tickets");
      }
    };

    const onContact = (data) => {
      if (data.action === "update") {
        setContact((prevState) => {
          if (prevState.id === data.contact?.id) {
            return { ...prevState, ...data.contact };
          }
          return prevState;
        });
      }
    };

    socket.on("ticket", onTicket);
    socket.on("contact", onContact);

    return () => {
      socket.off("connect", join);
      socket.off("ticket", onTicket);
      socket.off("contact", onContact);
      socket.emit("leaveChatBox", String(ticketId));
    };
  }, [ticketId]);

  const handleDrawerOpen = () => {
    setDrawerOpen(true);
  };

  const handleDrawerClose = () => {
    setDrawerOpen(false);
  };

  const handleChatSearchOpen = useCallback(() => setChatSearchOpen(true), []);
  const handleChatSearchClose = useCallback(() => {
    setChatSearchOpen(false);
    setInChatSearchMessageId(null);
  }, []);
  const handleGoToMessage = useCallback((msgId) => setInChatSearchMessageId(msgId), []);

  return (
    <div className={classes.root} id="drawer-container">
      <Paper
        variant="outlined"
        elevation={0}
        className={clsx(classes.mainWrapper, {
          [classes.mainWrapperShift]: drawerOpen,
        })}
      >
        <TicketHeader loading={loading || !ticketMatches}>
          <TicketInfo
            contact={contact}
            ticket={ticket}
            onClick={handleDrawerOpen}
            erpClient={erpClient}
            erpClients={erpClients}
            onSelectErpClient={selectErpClient}
            erpConfigured={erpConfigured}
            erpFrontendUrl={erpFrontendUrl}
            erpPendingCounts={erpPendingCounts}
          />
          <TicketActionButtons
            ticket={ticket}
            onReturnToQueue={onReturnToQueue}
            onSearchClick={handleChatSearchOpen}
          />
        </TicketHeader>
        <div className={classes.chatBody}>
          <ReplyMessageProvider>
            {chatSearchOpen && (
              <ChatSearchBar
                ticketId={ticketId}
                onGoToMessage={handleGoToMessage}
                onClose={handleChatSearchClose}
              />
            )}
            <MessagesList
              ticketId={ticketId}
              ticket={ticket}
              isGroup={ticket.isGroup}
              focusMessageId={messageIdFromSearch}
              inChatSearchMessageId={inChatSearchMessageId}
              onSearchClick={handleChatSearchOpen}
            />
            <MessageInput
              ticketStatus={ticket.status}
              phoneNumber={contact.number}
              onSearchClick={handleChatSearchOpen}
            />
          </ReplyMessageProvider>
        </div>
      </Paper>
      <ContactDrawer
        open={drawerOpen}
        handleDrawerClose={handleDrawerClose}
        contact={contact}
        loading={loading || erpLoading}
        erpClient={erpClient}
        erpConfigured={erpConfigured}
      />
    </div>
  );
};

export default Ticket;
